/**
 * 74.16 (DECISIONS 385): the aurora drawn — one additive mesh a world whose profile shows one (core/aurora.ts): three
 * curtains on an oval about the magnetic pole, their feet over the air's rim. The vertex shader places and folds them
 * (no per-frame vertex work: the frame sets four uniforms). It replaces the environmental effect's six rings about world
 * +y (environmentModels.ts buildAurora), which stood from under the ground to 1.38 R and were 361's sheets at head height.
 *
 * When (385 C): with the stars — the haze's `starsShown` (night, inside the air; 1 in space) × (1 − 0.85 × the weather's
 * tint) — and per vertex only where the sun is under that place's horizon, so from orbit the day side shows none.
 */
import * as THREE from 'three';
import type { PlanetState } from '../types';
import type { StarSystem, SystemBody } from '../core/systemGen';
import { auroraOf, type AuroraProfile } from '../core/aurora';

/** The look's dials: the curtains a world, the steps round the oval, the brightness per unit of strength, the weather's dimming. */
export const AURORA_LOOK = { curtains: 3, steps: 720, gain: 0.15, maxStrength: 1.6, overcast: 0.85 } as const;

const VERT = /* glsl */`
uniform vec3 uPole, uE1, uE2, uSun;
uniform float uR, uColat, uBottom, uTop, uTime, uSeed;
varying float vV, vU, vNight;
// position: (the angle round the oval, the height 0 foot – 1 top, the curtain)
void main() {
  float th = position.x, v = position.y, c = position.z;
  float fold = 0.035 * sin(3.0 * th + uTime * 0.05 + c * 1.7 + uSeed) + 0.015 * sin(7.0 * th - uTime * 0.11 + c * 2.3 + uSeed * 0.3) + 0.006 * sin(19.0 * th + uTime * 0.4 + c);
  float col = uColat + (c - 1.0) * 0.018 + fold;
  vec3 dir = normalize(uPole * cos(col) + (uE1 * cos(th) + uE2 * sin(th)) * sin(col));
  vV = v; vU = th;
  vNight = smoothstep(0.08, -0.18, dot(dir, uSun));   // the night side only: the sun under this place's horizon
  gl_Position = projectionMatrix * modelViewMatrix * vec4(dir * uR * mix(uBottom, uTop, v), 1.0);
}`;
const FRAG = /* glsl */`
uniform vec3 uLow, uHigh;
uniform float uGain, uTime;
varying float vV, vU, vNight;
void main() {
  float ray = 0.5 + 0.5 * sin(vU * 180.0 + uTime * 0.7 + 3.0 * sin(vU * 37.0 + uTime * 0.3));
  float foot = smoothstep(0.0, 0.07, vV), fade = pow(1.0 - vV, 1.5);
  vec3 col = mix(uLow, uHigh, smoothstep(0.3, 1.0, vV));
  gl_FragColor = vec4(col * uGain * vNight * foot * fade * (0.45 + 0.55 * ray), 1.0);
}`;

let shared: { position: THREE.BufferAttribute; index: THREE.BufferAttribute } | null = null;
/** The oval's strips, the same for every world (the shader takes the world's size and place): (angle, height, curtain) per vertex. */
function ovalBuffers(): { position: THREE.BufferAttribute; index: THREE.BufferAttribute } {
  if (shared) return shared;
  const { curtains, steps } = AURORA_LOOK;
  const pos = new Float32Array(curtains * (steps + 1) * 2 * 3), index: number[] = [];
  let k = 0;
  for (let c = 0; c < curtains; c++) {
    const base = c * (steps + 1) * 2;
    for (let i = 0; i <= steps; i++) for (let v = 0; v < 2; v++) { pos[k++] = (i / steps) * Math.PI * 2; pos[k++] = v; pos[k++] = c; }
    for (let i = 0; i < steps; i++) { const a = base + i * 2; index.push(a, a + 2, a + 1, a + 2, a + 3, a + 1); }
  }
  shared = { position: new THREE.BufferAttribute(pos, 3), index: new THREE.BufferAttribute(new Uint32Array(index), 1) };
  return shared;
}

/** One world's aurora. */
export class AuroraOval {
  readonly mesh: THREE.Mesh;
  readonly material: THREE.ShaderMaterial;
  readonly pole: THREE.Vector3;
  readonly e1: THREE.Vector3;
  readonly e2: THREE.Vector3;
  constructor(readonly profile: AuroraProfile, readonly planetIndex: number, radius: number) {
    this.pole = new THREE.Vector3(profile.pole.x, profile.pole.y, profile.pole.z).normalize();
    this.e1 = this.pole.clone().cross(Math.abs(this.pole.x) < 0.9 ? new THREE.Vector3(1, 0, 0) : new THREE.Vector3(0, 0, 1)).normalize();
    this.e2 = this.pole.clone().cross(this.e1);
    const { position, index } = ovalBuffers();
    const geo = new THREE.BufferGeometry();
    geo.setAttribute('position', position);
    geo.setIndex(index);
    geo.boundingSphere = new THREE.Sphere(new THREE.Vector3(), radius * profile.top * 1.01);   // (the positions are packed: the shader places them)
    this.material = new THREE.ShaderMaterial({
      vertexShader: VERT, fragmentShader: FRAG, transparent: true, depthWrite: false, side: THREE.DoubleSide, blending: THREE.AdditiveBlending,
      uniforms: {
        uPole: { value: this.pole }, uE1: { value: this.e1 }, uE2: { value: this.e2 }, uSun: { value: new THREE.Vector3(0, 1, 0) },
        uR: { value: radius }, uColat: { value: profile.colat }, uBottom: { value: profile.bottom }, uTop: { value: profile.top },
        uTime: { value: 0 }, uSeed: { value: profile.seed }, uGain: { value: 0 },
        uLow: { value: new THREE.Color(profile.colours.low) }, uHigh: { value: new THREE.Color(profile.colours.high) },
      },
    });
    this.mesh = new THREE.Mesh(geo, this.material);
    this.mesh.raycast = () => {};
    this.mesh.name = 'aurora';
  }
  /** The oval's foot at angle `th` round it, `off` radians further from the pole (unit direction from the centre). */
  footAt(th: number, off = 0): THREE.Vector3 {
    const c = this.profile.colat + off;
    return this.pole.clone().multiplyScalar(Math.cos(c)).addScaledVector(this.e1.clone().multiplyScalar(Math.cos(th)).addScaledVector(this.e2, Math.sin(th)), Math.sin(c)).normalize();
  }
  /** The angle round the oval nearest a direction from the centre. */
  angleOf(d: { x: number; y: number; z: number }): number { const v = new THREE.Vector3(d.x, d.y, d.z); return Math.atan2(v.dot(this.e2), v.dot(this.e1)); }
  /** This frame: the sun, how far the stars show and the weather's tint (385 C), the time; returns the share shown. */
  update(sun: THREE.Vector3, starsShown: number, overcast: number, time: number): number {
    const shown = Math.max(0, starsShown * (1 - AURORA_LOOK.overcast * Math.max(0, Math.min(1, overcast))));
    const u = this.material.uniforms;
    u.uSun.value.copy(sun);
    u.uGain.value = AURORA_LOOK.gain * Math.min(AURORA_LOOK.maxStrength, this.profile.strength) * shown;
    u.uTime.value = time;
    this.mesh.visible = shown > 0;
    return shown;
  }
  dispose(): void { this.mesh.geometry.dispose(); this.material.dispose(); }   // (the shared buffers stay: three uploads them once a context)
}

/** The sky's system and the world it stands on (rendering/systemSkyPlay.ts): the battle world's star and orbit. */
export interface AuroraSky { system: StarSystem; body: SystemBody; planetIndex: number }

/** Every world's aurora in the scene: a world shows one by its profile (the battle world by its sky's star; others as at Earth's light). */
export class PlanetAuroras {
  readonly ovals: AuroraOval[] = [];
  constructor(private readonly scene: THREE.Scene, planets: readonly PlanetState[], sky: AuroraSky | null) {
    planets.forEach((p, i) => {
      if (p.type !== 'planet' || !p.heightmap) return;
      const prof = sky && sky.planetIndex === i ? auroraOf(p, sky.system, sky.body) : auroraOf(p);
      if (!prof.shown) return;
      const o = new AuroraOval(prof, i, p.radius);
      o.mesh.position.set(p.position.x, p.position.y, p.position.z);
      scene.add(o.mesh);
      this.ovals.push(o);
    });
  }
  update(planets: readonly PlanetState[], sun: THREE.Vector3, starsShown: number, overcast: number, time: number): void {
    for (const o of this.ovals) {
      const p = planets[o.planetIndex];
      if (!p || p.alive === false) { o.mesh.visible = false; continue; }
      o.mesh.position.set(p.position.x, p.position.y, p.position.z);
      o.update(sun, starsShown, overcast, time);
    }
  }
  dispose(): void { for (const o of this.ovals) { this.scene.remove(o.mesh); o.dispose(); } this.ovals.length = 0; }
}
